/**
 * The ONE realtime topic builder (TO-468, ADR-0049), shared by the IoT custom
 * authorizer (grant), the notification-worker publisher (publish) and the browser
 * (subscribe) so the three can never disagree on topic shape.
 *
 * Shape: `{app}/{stage}/{tenantId}/user/{userId}/{entityType}`. `{app}/{stage}`
 * is the ENTIRE isolation boundary between deployments sharing one IoT endpoint
 * per account+region, so it comes from deployment config — never client input.
 */

/**
 * Leaf used in the IAM policy GRANT. `iot:Subscribe`/`iot:Receive` resource ARNs
 * treat `*` as a wildcard. In MQTT `*` is a literal — do not subscribe with it.
 */
export const REALTIME_ENTITY_TYPE_ALL = '*'

/**
 * Leaf used in the client MQTT SUBSCRIBE. `#` is MQTT's multi-level wildcard; the
 * IAM grant ending in `*` matches this filter string. Mixing the two up yields a
 * subscription that is accepted yet delivers nothing.
 */
export const REALTIME_SUBSCRIBE_LEAF = '#'

/** AWS IoT Core limits on a topic name / filter. */
export const REALTIME_TOPIC_MAX_BYTES = 256
export const REALTIME_TOPIC_MAX_SLASHES = 7

/** A topic segment or the assembled topic broke a rule. Fail closed: never sanitize. */
export class RealtimeTopicError extends Error {
	constructor(message: string) {
		super(message)
		this.name = 'RealtimeTopicError'
	}
}

export interface BuildUserTopicArgs {
	app: string
	stage: string
	tenantId: string
	userId: string
	/** An `EventEntityType` value, or one of the two wildcard leaves above. */
	entityType: string
}

// Identity / prefix segments may hold no MQTT wildcard, no IAM wildcard and no
// level separator: any of them would widen or move the topic into another subtree.
const FORBIDDEN_IN_SEGMENT = /[/#+*]/

// A segment is unsafe when it is blank (empty or whitespace-only — it names no
// subtree), carries a wildcard/separator, or carries a control character. MQTT
// forbids NUL (U+0000) outright and treats the other C0 (U+0000–U+001F) / DEL
// (U+007F) controls as malformed, so a topic carrying one is rejected or routed
// unpredictably by the broker. Checked by code point so no control character
// has to appear in a regex literal.
function hasControlChar(value: string): boolean {
	for (let i = 0; i < value.length; i++) {
		const code = value.charCodeAt(i)
		if (code <= 0x1f || code === 0x7f) return true
	}
	return false
}

function isUnsafeSegment(value: string): boolean {
	return value.trim().length === 0 || FORBIDDEN_IN_SEGMENT.test(value) || hasControlChar(value)
}

function assertSegment(name: string, value: string): void {
	if (isUnsafeSegment(value)) {
		throw new RealtimeTopicError(`realtime topic: invalid ${name} segment`)
	}
}

// The leaf may additionally BE one of the two wildcards (exactly), never contain one.
function assertLeaf(value: string): void {
	if (value === REALTIME_ENTITY_TYPE_ALL || value === REALTIME_SUBSCRIBE_LEAF) return
	if (isUnsafeSegment(value)) {
		throw new RealtimeTopicError('realtime topic: invalid entityType segment')
	}
}

/**
 * Build a user's realtime topic. Throws {@link RealtimeTopicError} — never
 * sanitizes — on an empty or unsafe segment, or when the topic breaks IoT's
 * 256-byte / 7-slash limits: sanitizing would build a valid topic in the WRONG
 * subtree, while a throw fails closed.
 */
export function buildUserTopic({ app, stage, tenantId, userId, entityType }: BuildUserTopicArgs): string {
	assertSegment('app', app)
	assertSegment('stage', stage)
	assertSegment('tenantId', tenantId)
	assertSegment('userId', userId)
	assertLeaf(entityType)

	const topic = `${app}/${stage}/${tenantId}/user/${userId}/${entityType}`
	if (new TextEncoder().encode(topic).length > REALTIME_TOPIC_MAX_BYTES) {
		throw new RealtimeTopicError(`realtime topic: exceeds ${REALTIME_TOPIC_MAX_BYTES} bytes`)
	}
	if (topic.split('/').length - 1 > REALTIME_TOPIC_MAX_SLASHES) {
		throw new RealtimeTopicError(`realtime topic: exceeds ${REALTIME_TOPIC_MAX_SLASHES} levels`)
	}
	return topic
}

/**
 * True when `prefix` is a well-formed `{app}/{stage}`: exactly one `/`, a
 * non-empty part on each side, and no wildcard.
 */
export function isValidTopicPrefix(prefix: string): boolean {
	const parts = prefix.split('/')
	return parts.length === 2 && parts.every((p) => !isUnsafeSegment(p))
}
